import { ScriptRelationalData, ScriptTable, ScriptAttribute } from '../model/ErModel';

/**
 * Converteix text en format script:
 * EQUIPO (nombre_eq, dirección, telefono, presidente)
 * ESTADIO (nombre_est, dirección_est, capacidad, nombre_eq (EQUIPO))
 * 
 * a una estructura de dades ScriptRelationalData
 * Suporta claus primàries amb prefixos/sufixos comuns si s'escriuen en text pla:
 * - Text: `*nombre_eq*` o `PK(nombre_eq)` o marcatge d'atribut
 * - O a través d'estructura JSON directa
 */
export const parseScriptTextToData = (text: string): ScriptRelationalData => {
    const lines = (text || '').split('\n').map(l => l.trim()).filter(l => l.length > 0 && !l.startsWith('#') && !l.startsWith('--'));
    const tables: ScriptTable[] = [];

    for (const line of lines) {
        // Ex: "EQUIPO (nombre_eq, dirección, telefono)"
        const parenIndex = line.indexOf('(');
        if (parenIndex === -1) continue;

        const tableName = line.substring(0, parenIndex).trim();
        let body = line.substring(parenIndex + 1);
        if (body.endsWith(')')) {
            body = body.substring(0, body.length - 1);
        }

        // Dividir per comes tenint en compte que una FK pot tenir parèntesis: "nombre_eq (EQUIPO)"
        const attrTokens: string[] = [];
        let currentToken = '';
        let insideParen = false;

        for (let i = 0; i < body.length; i++) {
            const char = body[i];
            if (char === '(') insideParen = true;
            else if (char === ')') insideParen = false;

            if (char === ',' && !insideParen) {
                attrTokens.push(currentToken.trim());
                currentToken = '';
            } else {
                currentToken += char;
            }
        }
        if (currentToken.trim().length > 0) {
            attrTokens.push(currentToken.trim());
        }

        const attributes: ScriptAttribute[] = attrTokens.map(token => {
            let isPK = false;
            let raw = token;

            // Detecció de marques de PK en text: *campo*, _campo_, o [PK] campo
            if (raw.startsWith('*') && raw.endsWith('*')) {
                isPK = true;
                raw = raw.slice(1, -1).trim();
            } else if (raw.toLowerCase().startsWith('pk_') || raw.toLowerCase().startsWith('id_') && !raw.includes('(')) {
                // Heurística de nom si no està marcat explícitament
            }

            // Detecció de FK: "nombre_eq (EQUIPO)" o "id_estadio(ESTADIO)"
            let fkTargetTable: string | null = null;
            const fkMatch = raw.match(/^(.*?)\s*\((.*?)\)$/);
            let attrName = raw;

            if (fkMatch) {
                attrName = fkMatch[1].trim();
                fkTargetTable = fkMatch[2].trim();
            }

            // Netejar marcadors residuals
            if (attrName.startsWith('*') && attrName.endsWith('*')) {
                isPK = true;
                attrName = attrName.slice(1, -1).trim();
            }

            return {
                name: attrName,
                isPK,
                fkTargetTable: fkTargetTable || null
            };
        });

        // IMPORTANT: No es marca cap atribut com a PK automàticament; ho han de definir els alumnes manualment


        tables.push({
            name: tableName,
            attributes
        });
    }

    return {
        type: 'Script',
        version: 1,
        tables
    };
};

/**
 * Converteix una estructura ScriptRelationalData a text editable
 */
export const formatDataToScriptText = (data: ScriptRelationalData): string => {
    if (!data || !Array.isArray(data.tables)) return '';

    return data.tables.map(table => {
        const attrs = (table.attributes || []).map(a => {
            let item = a.name;
            if (a.isPK) {
                item = `*${item}*`;
            }
            if (a.fkTargetTable) {
                item = `${item} (${a.fkTargetTable})`;
            }
            return item;
        }).join(', ');

        return `${table.name} (${attrs})`;
    }).join('\n');
};

import { DesignTestsConfig, RubricItem } from '../model/DesignTestConfig';

export interface ScriptValidationResult {
    score: number; // 0 a 100
    notaBase?: number;
    penalitzacioTotal?: number;
    intentsFallitsPrevis?: number;
    isCorrect: boolean;
    feedback: Array<{
        type: 'success' | 'error' | 'warning';
        message: string;
        diffType?: 'missing' | 'surplus' | 'mismatch' | 'correct';
        itemLabel?: string;
        expected?: string;
        actual?: string;
        table?: string;
        pointsEarned?: number;
        maxPoints?: number;
    }>;
    summary: {
        tablesExpected: number;
        tablesMatched: number;
        pksExpected: number;
        pksMatched: number;
        fksExpected: number;
        fksMatched: number;
    };
}

const norm = (s?: string) => (s || '').trim().toLowerCase();

/**
 * Valida un Script Relacional independentment de l'ordre de les taules i dels atributs
 * Suporta rúbrica configurable i penalització per intents fallits previs.
 */
export const validateScriptRelational = (
    studentData: ScriptRelationalData,
    solutionData: ScriptRelationalData,
    customConfig?: DesignTestsConfig | null,
    intentsFallitsPrevis: number = 0
): ScriptValidationResult => {
    const feedback: ScriptValidationResult['feedback'] = [];

    const solTables = solutionData.tables || [];
    const stuTables = studentData.tables || [];

    const rubricMap = new Map<string, RubricItem>();
    // Índex normalitzat per casar ítems de rúbrica desats amb formats d'ID antics
    // (prefixos / majúscules) amb els IDs que genera el validador actual.
    const rubricNormMap = new Map<string, RubricItem>();
    const normalizeRubricKey = (raw: string): string => {
        let k = (raw || '').toLowerCase();
        k = k.replace(/^(table_|column_|pk_|fk_|attr_|rel_table_|rel_col_|rel_fk_|t_|c_)/, '');
        return k.trim();
    };
    const hasCustomRubric = Boolean(customConfig && Array.isArray(customConfig.items) && customConfig.items.length > 0);
    if (hasCustomRubric && customConfig?.items) {
        customConfig.items.forEach(it => {
            rubricMap.set(it.id, it);
            rubricNormMap.set(normalizeRubricKey(it.id), it);
            if ((it as any).targetKey) rubricNormMap.set(normalizeRubricKey(String((it as any).targetKey)), it);
        });
    }

    let earnedScore = 0;
    let totalMaxConfigured = 0;

    const checkRubric = (candidates: string | string[], defaultMax = 0): { enabled: boolean; points: number } => {
        if (!hasCustomRubric) {
            return { enabled: true, points: defaultMax };
        }
        const list = Array.isArray(candidates) ? candidates : [candidates];
        let item: RubricItem | undefined;
        for (const c of list) {
            item = rubricMap.get(c) || rubricNormMap.get(normalizeRubricKey(c));
            if (item) break;
        }
        if (!item || !item.enabled) {
            return { enabled: false, points: 0 };
        }
        return { enabled: true, points: Number(item.points) || 0 };
    };

    let matchedTables = 0;
    let expectedPKs = 0;
    let matchedPKs = 0;
    let expectedFKs = 0;
    let matchedFKs = 0;

    solTables.forEach(solT => {
        const tableRubricId = `table_${solT.name}`;
        const tableRubric = checkRubric([tableRubricId, solT.name], 30);
        if (tableRubric.enabled) totalMaxConfigured += tableRubric.points;

        const foundStuT = stuTables.find(t => norm(t.name) === norm(solT.name));
        if (!foundStuT) {
            if (tableRubric.enabled) {
                feedback.push({
                    type: 'error',
                    diffType: 'missing',
                    itemLabel: solT.name,
                    expected: `Taula "${solT.name}"`,
                    actual: '(No entregada)',
                    message: `Falta la taula "${solT.name}".`,
                    table: solT.name,
                    maxPoints: tableRubric.points,
                    pointsEarned: 0
                });
            }
            return;
        }

        matchedTables++;
        if (tableRubric.enabled) {
            earnedScore += tableRubric.points;
            feedback.push({
                type: 'success',
                diffType: 'correct',
                itemLabel: solT.name,
                expected: `Taula "${solT.name}"`,
                actual: `Taula "${foundStuT.name}"`,
                message: `Taula "${solT.name}" present (+${tableRubric.points} pts).`,
                table: solT.name,
                maxPoints: tableRubric.points,
                pointsEarned: tableRubric.points
            });
        }

        const solAttrs = solT.attributes || [];
        const stuAttrs = foundStuT.attributes || [];

        // Validar atributs d'aquesta taula
        solAttrs.forEach(solA => {
            if (solA.isPK) expectedPKs++;
            if (solA.fkTargetTable) expectedFKs++;

            const attrRubricId = `attr_${solT.name}.${solA.name}`;
            const attrRubricAlts = [attrRubricId, `${solT.name}.${solA.name}`, solA.name];
            const defaultPoints = solA.isPK ? 35 : (solA.fkTargetTable ? 35 : 20);
            const attrRubric = checkRubric(attrRubricAlts, defaultPoints);
            if (attrRubric.enabled) totalMaxConfigured += attrRubric.points;

            const foundA = stuAttrs.find(a => norm(a.name) === norm(solA.name));
            if (!foundA) {
                if (attrRubric.enabled) {
                    feedback.push({
                        type: 'error',
                        diffType: 'missing',
                        itemLabel: `${solT.name}.${solA.name}`,
                        expected: `Atribut "${solA.name}" ${solA.isPK ? '[PK]' : ''} ${solA.fkTargetTable ? `(FK ➔ ${solA.fkTargetTable})` : ''}`,
                        actual: '(No entregat)',
                        message: `A la taula "${solT.name}" falta l'atribut "${solA.name}".`,
                        table: solT.name,
                        maxPoints: attrRubric.points,
                        pointsEarned: 0
                    });
                }
                return;
            }

            let attrCorrect = true;

            // Comprovar PK
            if (Boolean(solA.isPK) !== Boolean(foundA.isPK)) {
                if (attrRubric.enabled) {
                    feedback.push({
                        type: 'error',
                        diffType: 'mismatch',
                        itemLabel: `${solT.name}.${solA.name} (PK)`,
                        expected: solA.isPK ? 'Clau Primària (PK)' : 'No PK',
                        actual: foundA.isPK ? 'Clau Primària (PK)' : 'No PK',
                        message: `A la taula "${solT.name}", l'atribut "${solA.name}" ${solA.isPK ? 'ha de ser' : 'no ha de ser'} Clau Primària (PK).`,
                        table: solT.name
                    });
                }
                attrCorrect = false;
            } else if (solA.isPK) {
                matchedPKs++;
            }

            // Comprovar FK
            if (solA.fkTargetTable) {
                if (!foundA.fkTargetTable) {
                    if (attrRubric.enabled) {
                        feedback.push({
                            type: 'error',
                            diffType: 'mismatch',
                            itemLabel: `${solT.name}.${solA.name} (FK)`,
                            expected: `Referència FK a "${solA.fkTargetTable}"`,
                            actual: '(Sense referència FK)',
                            message: `A la taula "${solT.name}", l'atribut "${solA.name}" ha de referenciar la taula "${solA.fkTargetTable}".`,
                            table: solT.name
                        });
                    }
                    attrCorrect = false;
                } else if (norm(foundA.fkTargetTable) !== norm(solA.fkTargetTable)) {
                    if (attrRubric.enabled) {
                        feedback.push({
                            type: 'error',
                            diffType: 'mismatch',
                            itemLabel: `${solT.name}.${solA.name} (FK)`,
                            expected: `Referència FK a "${solA.fkTargetTable}"`,
                            actual: `Referència FK a "${foundA.fkTargetTable}"`,
                            message: `A la taula "${solT.name}", l'atribut "${solA.name}" referencia "${foundA.fkTargetTable}", però s'esperava "${solA.fkTargetTable}".`,
                            table: solT.name
                        });
                    }
                    attrCorrect = false;
                } else {
                    matchedFKs++;
                }
            } else if (foundA.fkTargetTable) {
                if (attrRubric.enabled) {
                    feedback.push({
                        type: 'warning',
                        diffType: 'mismatch',
                        itemLabel: `${solT.name}.${solA.name} (FK)`,
                        expected: 'Sense FK',
                        actual: `Apunta a "${foundA.fkTargetTable}"`,
                        message: `A la taula "${solT.name}", l'atribut "${solA.name}" no hauria de tenir clau forana (apunta a "${foundA.fkTargetTable}").`,
                        table: solT.name
                    });
                }
                attrCorrect = false;
            }

            if (attrCorrect) {
                if (attrRubric.enabled) {
                    earnedScore += attrRubric.points;
                    feedback.push({
                        type: 'success',
                        diffType: 'correct',
                        itemLabel: `${solT.name}.${solA.name}`,
                        expected: `Camp "${solA.name}"`,
                        actual: `Camp "${foundA.name}"`,
                        message: `Camp "${solA.name}" a "${solT.name}" correcte (+${attrRubric.points} pts).`,
                        table: solT.name,
                        maxPoints: attrRubric.points,
                        pointsEarned: attrRubric.points
                    });
                }
            } else if (attrRubric.enabled) {
                feedback.push({
                    type: 'error',
                    diffType: 'mismatch',
                    itemLabel: `${solT.name}.${solA.name}`,
                    expected: 'Totes les propietats correctes',
                    actual: 'Propietats diferents a la solució',
                    message: `Errors a l'atribut "${solA.name}" de "${solT.name}".`,
                    table: solT.name,
                    maxPoints: attrRubric.points,
                    pointsEarned: 0
                });
            }
        });

        // Detectar atributs sobrants
        stuAttrs.forEach(stuA => {
            if (!solAttrs.some(a => norm(a.name) === norm(stuA.name))) {
                feedback.push({
                    type: 'warning',
                    diffType: 'surplus',
                    itemLabel: `${solT.name}.${stuA.name}`,
                    expected: '(No requerit a la solució)',
                    actual: `Atribut "${stuA.name}"`,
                    message: `A la taula "${solT.name}", l'atribut "${stuA.name}" no és requerit.`,
                    table: solT.name
                });
            }
        });
    });

    // Detectar taules sobrants
    stuTables.forEach(stuT => {
        if (!solTables.some(t => norm(t.name) === norm(stuT.name))) {
            feedback.push({
                type: 'warning',
                diffType: 'surplus',
                itemLabel: stuT.name,
                expected: '(No requerida a la solució)',
                actual: `Taula "${stuT.name}"`,
                message: `La taula "${stuT.name}" no és requerida a la solució.`,
                table: stuT.name
            });
        }
    });

    // Si la solució del professor està buida no es pot avaluar (0, no 100).
    const solucioBuida = solTables.length === 0;

    // Nota estructural (pesos per defecte), independent de la rúbrica.
    const totalPoints = (solTables.length * 30) + (expectedPKs * 35) + (expectedFKs * 35);
    const earnedPoints = (matchedTables * 30) + (matchedPKs * 35) + (matchedFKs * 35);
    const notaEstructural = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;

    let notaBase = 0;
    if (solucioBuida) {
        notaBase = 0;
    } else if (hasCustomRubric && totalMaxConfigured > 0) {
        // Només fem servir la rúbrica si realment aporta punts avaluables; si no, caiem a l'estructural.
        notaBase = Math.round((earnedScore / totalMaxConfigured) * 100);
    } else {
        notaBase = notaEstructural;
    }

    const penalitzacioPerIntent = Number(customConfig?.penalitzacioPerIntent) || 0;
    const penalitzacioTotal = penalitzacioPerIntent * Math.max(0, intentsFallitsPrevis);
    const scoreFinal = Math.max(0, Math.min(100, Math.round(notaBase - penalitzacioTotal)));

    if (penalitzacioTotal > 0) {
        feedback.push({
            type: 'warning',
            message: `Penalització aplicada: -${penalitzacioTotal} punts (-${penalitzacioPerIntent} pts per ${intentsFallitsPrevis} intents fallits previs).`
        });
    }

    if (feedback.length === 0) {
        feedback.push({
            type: 'success',
            message: 'Script Relacional completament correcte!'
        });
    }

    return {
        score: scoreFinal,
        notaBase,
        penalitzacioTotal,
        intentsFallitsPrevis,
        isCorrect: scoreFinal === 100 && feedback.every(f => f.type !== 'error'),
        feedback,
        summary: {
            tablesExpected: solTables.length,
            tablesMatched: matchedTables,
            pksExpected: expectedPKs,
            pksMatched: matchedPKs,
            fksExpected: expectedFKs,
            fksMatched: matchedFKs
        }
    };
};
